// Subscription-based operational access (UI side).
//
// ONE rule for the whole client UI: may the customer of this store perform OPERATIONAL actions (activate a branch or an
// application, raise operational requests, send menus to applications, ...) today? The server answers it from the actual
// Subscription and branch coverage (`branchCoverage.access` of GET /billing/me/subscription-summary, see
// lud-backend/services/branchActivationView.operationalAccessOf); nothing here re-derives billing. Role is never the question:
// an expired `store` is locked exactly like a `new` client, and an admin-granted subscription counts like a paid one.
//
//   state "active"    a branch is covered today (paid, admin-granted, converting grant inside its free window)
//   state "legacy"    runs operational branches but never had a subscription record: not locked until an administrator decides
//   state "inactive"  never subscribed, expired, future-dated, unpaid or overdue: operational actions are locked
//   state "unknown"   not loaded (yet) or unavailable: nothing is locked in the UI (the server stays the authority)
//
// Locking only ever disables the OPERATIONAL actions of a page. Viewing, onboarding information, draft branches, the subscription
// pages, receipts and support are never locked.

export const BILLING_PATH = "/auth/billing";

export const OPERATIONAL_STATES = Object.freeze({
  ACTIVE: "active",
  LEGACY: "legacy",
  INACTIVE: "inactive",
  UNKNOWN: "unknown",
});

/** Reads the server's operational-access answer from one subscription summary (never throws). */
export const operationalAccessFromSummary = (summary) => {
  const access = summary?.branchCoverage?.access;
  if (!access || typeof access.state !== "string") return { state: OPERATIONAL_STATES.UNKNOWN, operational: null, reason: null };
  const state = Object.values(OPERATIONAL_STATES).includes(access.state) ? access.state : OPERATIONAL_STATES.UNKNOWN;
  return {
    state,
    operational: state === OPERATIONAL_STATES.INACTIVE ? false : state === OPERATIONAL_STATES.UNKNOWN ? null : true,
    reason: access.reason || null,
  };
};

export const SUBSCRIPTION_TEXT = {
  ar: {
    title: "حسابك جاهز. فعّل اشتراكك للبدء باستخدام الخدمات التشغيلية.",
    action: "الاشتراك والفوترة",
    lockedAction: "هذا الإجراء يتطلب اشتراكًا فعّالًا",
    reasons: {
      no_subscription: "لا يوجد اشتراك فعّال لهذا المتجر بعد.",
      not_covered: "فروع هذا المتجر غير مشمولة بأي اشتراك فعّال.",
      expired: "انتهى اشتراك هذا المتجر. جدّد الاشتراك لمتابعة استخدام الخدمات التشغيلية.",
      renewal_overdue: "فاتورة التجديد متأخرة السداد. سدّدها لإعادة تفعيل الخدمات التشغيلية.",
      awaiting_payment: "الاشتراك بانتظار سداد الفاتورة وتأكيد الدفع.",
      pending_start: "الاشتراك لم يبدأ بعد، وستتوفر الخدمات التشغيلية عند بدايته.",
    },
    stillAvailable: "يمكنك مواصلة استعراض المعلومات وإكمال بيانات متجرك وإضافة الفروع كمسودات والتواصل مع الدعم.",
    pendingRole: "جارٍ تحديث صلاحيات حسابك بعد تفعيل الاشتراك. سيتم التحديث تلقائيًا.",
  },
  en: {
    title: "Your account is ready. Activate a subscription to start using operational features.",
    action: "Subscription & billing",
    lockedAction: "This action needs an active subscription",
    reasons: {
      no_subscription: "This store has no active subscription yet.",
      not_covered: "The branches of this store are not covered by an active subscription.",
      expired: "This store's subscription has ended. Renew it to keep using operational features.",
      renewal_overdue: "A renewal invoice is overdue. Pay it to restore operational features.",
      awaiting_payment: "The subscription is waiting for the invoice to be paid and the payment confirmed.",
      pending_start: "The subscription has not started yet. Operational features unlock when it does.",
    },
    stillAvailable: "You can keep browsing, complete your store information, add draft branches and contact support.",
    pendingRole: "Your account access is being updated after the subscription was activated. It will refresh automatically.",
  },
};

export const subscriptionText = (lang) => SUBSCRIPTION_TEXT[lang === "en" ? "en" : "ar"];

/** Why the operational actions are locked, in the user's language. */
export const lockReasonText = (reason, lang) => {
  const text = subscriptionText(lang);
  return text.reasons[reason] || text.title;
};
